/**
 * ew-rail.js — carousel controls for the Eyewinsome scroll-snap rails.
 *
 * Progressive enhancement over content that already works. The rail is a native CSS
 * scroll-snap track: with this file blocked it still scrolls by drag, wheel, trackpad and
 * keyboard, and every tile is a real <a> in the first paint. All this adds is the pair of
 * arrows and the dot row the GemPages designs draw.
 *
 * No library, no dependency, no external request — `docs/LIQUID-STANDARD.md` §0.
 *
 * Idempotent by design: a page may include this file once per rail section, so every root
 * is stamped `data-ew-rail-ready` and skipped on any later pass.
 *
 * Arrows are `visibility: hidden` until the carousel is hovered. That is the design, on the
 * true reference too (`docs/TRUE-REFERENCE.md` §4) — it is not a defect to fix. The CSS
 * also reveals them on :focus-within so a keyboard user can see what they are operating.
 */
(() => {
  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)');

  const scrollTo = (rail, left) => {
    rail.scrollTo({ left, behavior: REDUCE.matches ? 'auto' : 'smooth' });
  };

  const slideCentre = (rail, slide) =>
    slide.offsetLeft - rail.offsetLeft + slide.offsetWidth / 2;

  const nearestSlide = (rail) => {
    const mid = rail.scrollLeft + rail.clientWidth / 2;
    let best = 0;
    let bestGap = Infinity;
    [...rail.children].forEach((slide, i) => {
      const gap = Math.abs(slideCentre(rail, slide) - mid);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    return best;
  };

  const init = (root) => {
    if (root.hasAttribute('data-ew-rail-ready')) return;
    const rail = root.querySelector('[data-ew-rail-track]');
    if (!rail) return;
    root.setAttribute('data-ew-rail-ready', '');

    // One "page" of the track, not one slide: the designs step by roughly a screenful,
    // and scroll-snap then settles on the nearest slide boundary for us.
    const step = () => Math.max(1, rail.clientWidth * 0.9);

    const prev = root.querySelector('[data-ew-rail-prev]');
    const next = root.querySelector('[data-ew-rail-next]');
    if (prev) prev.addEventListener('click', () => scrollTo(rail, rail.scrollLeft - step()));
    if (next) next.addEventListener('click', () => scrollTo(rail, rail.scrollLeft + step()));

    const dots = [...root.querySelectorAll('[data-ew-rail-dot]')];
    dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        const slide = rail.children[i];
        if (slide) scrollTo(rail, slide.offsetLeft - rail.offsetLeft);
      });
    });

    const syncEnds = () => {
      const atStart = rail.scrollLeft <= 1;
      const atEnd = rail.scrollLeft + rail.clientWidth >= rail.scrollWidth - 1;
      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;
      if (dots.length) {
        const active = nearestSlide(rail);
        dots.forEach((d, i) => d.setAttribute('aria-selected', i === active ? 'true' : 'false'));
      }
    };

    rail.addEventListener('scroll', syncEnds, { passive: true });
    window.addEventListener('resize', syncEnds, { passive: true });
    syncEnds();
  };

  const scan = () => document.querySelectorAll('[data-ew-rail]').forEach(init);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan, { once: true });
  } else {
    scan();
  }

  // The theme editor re-renders a section without a page load.
  document.addEventListener('shopify:section:load', (event) => {
    event.target.querySelectorAll('[data-ew-rail]').forEach(init);
  });
})();
